if (!customElements.get('product-compare-button')) {
  customElements.define('product-compare-button', class ProductCompareButton extends HTMLElement {
    constructor() {
      super();

      this.button = this.querySelector('[data-add-button]');
      this.popoverPanel = this.querySelector('[data-popover]');
      this.addedView = this.querySelector('[data-added-view]');
      this.swapView = this.querySelector('[data-swap-view]');
      this.list = this.querySelector('[data-list]');
      this.template = this.querySelector('[data-item-template]');
      this.label = this.querySelector('[data-label]');

      this.defaultLabel = this.getAttribute('data-label');
      this.addedLabel = this.getAttribute('data-added-label');

      this.button.addEventListener('click', this.onButtonClick.bind(this));
      this.list.addEventListener('click', this.onSwapClick.bind(this));

      this.onDocumentClickBound = this.onDocumentClick.bind(this);
      this.onKeydownBound = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.refreshState();
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get product() {
      return {
        id: this.productId,
        title: this.getAttribute('data-product-title'),
        url: this.getAttribute('data-product-url'),
        image: this.getAttribute('data-product-image')
      };
    }

    get compareUrl() {
      return this.getAttribute('data-compare-url');
    }

    get autoRedirect() {
      return this.getAttribute('data-auto-redirect') === 'true';
    }

    get storageKey() {
      return `${theme.settings.themeName}:product-comparison`;
    }

    getItems() {
      if (!theme.config.hasLocalStorage) return [];

      try {
        const items = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
        return Array.isArray(items) ? items : [];
      }
      catch (error) {
        return [];
      }
    }

    setItems(items) {
      if (!theme.config.hasLocalStorage) return;

      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(items));
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.compareUpdate, { count: items.length });
      }
      catch (error) {
        // Safari private mode (or storage full) may throw here, fail silently
      }
    }

    refreshState() {
      const isAdded = this.getItems().some((item) => item.id === this.productId);
      this.button.classList.toggle('is-added', isAdded);
      if (this.label) {
        this.label.textContent = isAdded ? this.addedLabel : this.defaultLabel;
      }
    }

    onButtonClick(event) {
      event.preventDefault();

      const items = this.getItems();
      if (items.some((item) => item.id === this.productId)) {
        // Already added: nothing left to store, just take the customer to
        // the comparison page (if one is configured) instead of doing nothing.
        if (this.compareUrl) {
          window.location.href = this.compareUrl;
        }
        return;
      }

      if (items.length < 3) {
        items.push(this.product);
        this.setItems(items);
        this.refreshState();

        if (this.autoRedirect && this.compareUrl) {
          window.location.href = this.compareUrl;
          return;
        }

        this.openAddedMessage();
        return;
      }

      this.openSwapPicker(items);
    }

    openAddedMessage() {
      this.addedView.hidden = false;
      this.swapView.hidden = true;
      this.openPopoverPanel();

      clearTimeout(this.addedMessageTimeout);
      this.addedMessageTimeout = setTimeout(this.closePopover.bind(this), 4000);
    }

    openSwapPicker(items) {
      this.list.innerHTML = '';

      items.forEach((item, index) => {
        const fragment = this.template.content.cloneNode(true);
        const img = fragment.querySelector('img');
        const title = fragment.querySelector('.product-compare-button__title');
        const swapButton = fragment.querySelector('[data-swap]');

        img.src = item.image;
        img.alt = item.title;
        title.textContent = item.title;
        swapButton.setAttribute('data-index', index);

        this.list.appendChild(fragment);
      });

      this.addedView.hidden = true;
      this.swapView.hidden = false;
      this.openPopoverPanel();
    }

    openPopoverPanel() {
      this.popoverPanel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClickBound);
      document.addEventListener('keydown', this.onKeydownBound);
    }

    closePopover() {
      clearTimeout(this.addedMessageTimeout);
      this.popoverPanel.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClickBound);
      document.removeEventListener('keydown', this.onKeydownBound);
    }

    onSwapClick(event) {
      const swapButton = event.target.closest('[data-swap]');
      if (!swapButton) return;

      const index = parseInt(swapButton.getAttribute('data-index'), 10);
      const items = this.getItems();
      items[index] = this.product;
      this.setItems(items);

      this.closePopover();
      this.refreshState();
      this.redirectIfNeeded();
    }

    redirectIfNeeded() {
      if (this.autoRedirect && this.compareUrl) {
        window.location.href = this.compareUrl;
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closePopover();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closePopover();
        this.button.focus();
      }
    }
  });
}
